import React, { useContext, useEffect, useState } from 'react'
import { DeepPartial, ExtractObjects, useForm, FormProvider } from 'react-hook-form'
import './index.scss'
import { StoresContext } from '@App/contexts'
import { SuggestionStatus } from '@Components/MatchComponents/v2/suggestion-status'
import { observer } from 'mobx-react'
import { useSearchParams } from 'react-router'
import clsx from 'clsx'
import Sidebar from '@Components/Sidebars/Sidebar'
import Button from '@Components/Buttons/Button'
import { ICONS } from '@Const/common'
import SubmissionFilters from './SubmissionFilters'
import { SEARCH_PARAM } from '@App/navigation/param'
import { checkboxDefaults } from '@App/stores/rightsholder/match/match-history-store'

export type SubmissionFilterInputs = Record<SuggestionStatus, boolean | string>

interface SubmissionFilterSectionProps {
    /** Called once the sidebar's filter is the one to search with (Apply, Remove, or a URL-linked filter). */
    onSearch: () => void
}

const SubmissionFilterSection = observer((props: SubmissionFilterSectionProps) => {
    const { matchHistoryStore } = useContext(StoresContext)
    const { onSearch } = props
    const methods = useForm()
    const [searchParams, setSearchParams] = useSearchParams()
    const [isSidebarOpen, setIsSidebarOpen] = useState(false)

    useEffect(() => {
        const matchFilterQueryParam = searchParams.get(SEARCH_PARAM.matchHistoryFilter)
        if (matchFilterQueryParam) {
            const mergedDefaults = {
                ...checkboxDefaults,
                [matchFilterQueryParam.toString()]: true,
            }
            setSearchParams({})
            setFilters({ ...mergedDefaults })
            onSearch()
        }
        // Set values form based on the current filter in the store
        Object.keys(matchHistoryStore.matchSuggestionHistoryFilter).forEach((value: string) => {
            const status = value as keyof typeof matchHistoryStore.matchSuggestionHistoryFilter
            if (matchHistoryStore.matchSuggestionHistoryFilter[status]) {
                methods.setValue(`filter.${value}`, matchHistoryStore.matchSuggestionHistoryFilter[status])
            }
        })
    }, [])

    const setFilters = (
        filter:
            | (ExtractObjects<SubmissionFilterInputs> extends never
                  ? SubmissionFilterInputs
                  : DeepPartial<SubmissionFilterInputs>)
            | undefined
    ) => {
        if (filter) {
            // The store expects a fully-shaped SubmissionFilterInputs object; cast here
            // because the incoming filter may be a DeepPartial during intermediate updates.
            matchHistoryStore.setMatchSuggestionHistoryFilter(filter as SubmissionFilterInputs)
        }
    }
    const search = () => {
        setIsSidebarOpen(false)
        onSearch()
    }

    return (
        <div className="match-submission-filter">
            <FormProvider {...methods}>
                <div className={clsx('sidebar-wrapper', isSidebarOpen ? 'open' : 'closed')}>
                    <Sidebar onClose={() => setIsSidebarOpen(false)}>
                        <SubmissionFilters
                            handleFilterValueChange={setFilters}
                            onApplyFilter={search}
                            onResetFilter={search}
                        />
                    </Sidebar>
                </div>
                {!isSidebarOpen && (
                    <Button
                        type="secondary"
                        className="sidebar-button"
                        onClick={() => setIsSidebarOpen(true)}
                        mainColor
                    >
                        <>
                            <span className="button-text">
                                Filters ({matchHistoryStore.matchSuggestionHistoryFilterCount})
                            </span>
                            <img alt="filter" src={ICONS.filter} />
                        </>
                    </Button>
                )}
            </FormProvider>
        </div>
    )
})

export default SubmissionFilterSection
